import { imagesService } from '@/services/api'
import { cloneDeep, find } from 'lodash'
import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref } from 'vue'
import { useAlertStore } from '../system'

type Status = 'initial' | 'loading' | 'ready' | 'error'

/**
 * 404 (NOT_FOUND) and 410 (GONE) from an image endpoint both mean
 * "this image is no longer available" — typically because the binary was
 * deleted on the server but the ID is still referenced in a parent
 * entity's `images` relation (stale FK).  Treat these as a benign missing
 * resource and return null so callers can render a placeholder instead of
 * propagating an unhandled rejection that crashes flows (dashboard, building
 * lists, document previews, ImagePreview overlay, etc).
 */
function isImageMissingError(error: any): boolean {
	// The axios responseOnRejected interceptor in src/helpers/axios/interceptors.ts
	// rewraps every AxiosError into a flat error_info object with `status` directly
	// at the top level and re-rejects with that. Raw AxiosError (when no interceptor
	// modifies it) exposes status under `.response.status` or `.request.status`.
	// Check all three shapes so this helper is robust regardless of which layer
	// catches the rejection.
	const status =
		error?.response?.status ?? error?.request?.status ?? error?.status
	return status === 404 || status === 410
}

export const useImageStore = defineStore('image', () => {
	const data = ref([])
	const items = ref([])
	const status = ref<Status>('initial')

	function itemAdd(value) {
		items.value.push(value)
	}
	function itemRemove() {
		const index = items.value.findIndex((item) => item.id === id)
		if (index >= 0) {
			items.value.splice(index, 1)
		}
	}
	function statusSet(value: Status) {
		status.value = value
	}
	function getItem(id: number) {
		return find(items.value, { id })
	}

	function attachBuilding({ id, targetId }: { id: number; targetId: number }) {
		return imagesService.attachBuilding({ id, targetId }).then(
			({ data }) => {
				return data
			},
			(error) => {
				useAlertStore().errorAlert(error)
				return error
			}
		)
	}
	function detachBuilding(image_id: number, building_id: number) {
		return imagesService.detachBuilding(image_id, building_id).then(
			({ data }) => {
				return data
			},
			(error) => {
				useAlertStore().errorAlert(error)
				return error
			}
		)
	}
	function create({ binary, onUploadProgress = () => {} }) {
		statusSet('loading')
		const data = new FormData() //TODO sollte nicht nötig sein
		data.append('binary', binary)
		return imagesService
			.create({
				data: { binary },
				headers: {
					'Content-Type': 'multipart/form-data',
				},
				// onUploadProgress: (ev) => onUploadProgress(ev),// FIXME
			})
			.then(
				(image) => {
					statusSet('ready')
					return image
				},
				(error) => {
					statusSet('error')
					useAlertStore().errorAlert(error)
					throw error
				}
			)
	}

	function loadImageBlobWithInfo({ id }: { id: number }) {
		return imagesService
			.show(id)
			.then((info) => {
				return imagesService.binary(id).then((binary) => {
					return { binary, info }
				})
			})
			.catch((error) => {
				statusSet('error')
				if (isImageMissingError(error)) {
					return null
				}
				throw error
			})
	}
	function loadItem(id) : Promise<any> {
		if (typeof id === 'undefined' || id === 0) {
			return
		}
		const image = getItem(id)
		if (typeof image !== 'undefined') {
			return Promise.resolve( image)
		}
		statusSet('loading')
		return imagesService
			.show(id)
			.then((image) => {
				return imagesService.binary(id).then(
					(binary) => {
						const tmp = cloneDeep(image)
						const urlCreator = window.URL || window.webkitURL
						try {
							const imageUrl = urlCreator.createObjectURL(binary.data)
							tmp.data.imageUrl = imageUrl
						} catch (error) {
							console.log(error)
						}

						itemAdd(tmp.data)
						statusSet('ready')
						return tmp.data
					},
					(error) => {
						statusSet('error')
						//dispatch('error', error, { root: true })
						if (isImageMissingError(error)) {
							return null
						}
						throw error
					}
				)
			})
			.catch((error) => {
				statusSet('error')
				//dispatch('error', error, { root: true })
				if (isImageMissingError(error)) {
					return null
				}
				throw error
			})
	}
	const setMainImage = imagesService.setMainImage

	function remove(id) {
		statusSet('loading')
		return imagesService.delete(id).then(
			(data) => {
				itemRemove(id)
				statusSet('ready')
				return data
			},
			(error) => {
				statusSet('error')
				useAlertStore().errorAlert(error)
				return error
			}
		)
	}

	function $reset() {
		data.value = []
		items.value = []
	}

	return {
		data,
		items,
		status,
		itemAdd,
		itemRemove,
		statusSet,
		getItem,
		attachBuilding,
		detachBuilding,
		create,
		loadImageBlobWithInfo,
		loadItem,
		setMainImage,
		remove,
		$reset,
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useImageStore, import.meta.hot))
}
